import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { useAuth } from "../../context/AuthContext";
import {
  fetchBillsForCustomer,
  fetchPaymentsForCustomer,
  fetchAuditNotesForCustomer,
  addAuditNoteForCustomer,
  buildWhatsAppReminderMessage,
  whatsappUrlForPhone,
  effectiveDueDateForOverdue,
  daysOutstandingForBill,
  creditAgeStartDate,
  paymentTimestamp,
  creditBillBranch,
  creditPaymentBranch,
} from "../../services/creditDueService";
import { useCreditBranchFilter } from "./useCreditBranchFilter";
import CreditBranchSelector from "./CreditBranchSelector";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function statusClass(s) {
  if (s === "paid") return "bg-emerald-100 text-emerald-900";
  if (s === "partial") return "bg-amber-100 text-amber-900";
  return "bg-slate-100 text-slate-800";
}

function fmtDate(d) {
  if (d == null || d === "") return "—";
  if (typeof d === "string") {
    const t = d.trim();
    if (!t) return "—";
    const asDate = new Date(t);
    if (!Number.isNaN(asDate.getTime())) return asDate.toLocaleDateString("en-IN");
    return t;
  }
  if (typeof d === "number" && Number.isFinite(d)) {
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("en-IN");
  }
  if (d instanceof Date) {
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN");
  }
  if (typeof d.toMillis === "function") {
    try {
      const dt = new Date(d.toMillis());
      return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("en-IN");
    } catch {
      /* fall through */
    }
  }
  if (typeof d.toDate === "function") {
    try {
      return d.toDate().toLocaleDateString("en-IN");
    } catch {
      return "—";
    }
  }
  if (typeof d.seconds === "number") {
    return new Date(d.seconds * 1000).toLocaleDateString("en-IN");
  }
  if (typeof d._seconds === "number") {
    return new Date(d._seconds * 1000).toLocaleDateString("en-IN");
  }
  return "—";
}

function diffDays(start, end) {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  s.setHours(0, 0, 0, 0);
  e.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((e - s) / 86400000));
}

export default function CreditCustomerDetail() {
  const { customerId } = useParams();
  const { user: storedUser } = useAuth();
  const { branch, setBranch, visibleBranches } = useCreditBranchFilter(storedUser);

  const [bills, setBills] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [noteText, setNoteText] = useState("");
  const [lastContactDate, setLastContactDate] = useState("");
  const [nextActionDate, setNextActionDate] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [daysMode, setDaysMode] = useState(() => {
    try {
      return localStorage.getItem("creditCustomer_daysMode") === "credit" ? "credit" : "received";
    } catch {
      return "received";
    }
  });

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setErr("");
    try {
      const [rows, payRows, noteRows] = await Promise.all([
        fetchBillsForCustomer(customerId),
        fetchPaymentsForCustomer(customerId),
        fetchAuditNotesForCustomer(customerId),
      ]);
      setBills(rows);
      setPayments(payRows);
      setNotes(noteRows);
    } catch (e) {
      setErr(e?.message || "Failed to load bills.");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
  }, [load]);

  const billById = useMemo(() => {
    const m = new Map();
    for (const b of bills) {
      if (b?.id) m.set(b.id, b);
    }
    return m;
  }, [bills]);

  const billsBranch = useMemo(
    () => bills.filter((b) => creditBillBranch(b) === branch),
    [bills, branch]
  );

  const billsBranchTotals = useMemo(() => {
    let totalAmount = 0;
    let paidAmount = 0;
    let balanceAmount = 0;
    for (const b of billsBranch) {
      totalAmount += round2(b.totalAmount);
      paidAmount += round2(b.paidAmount);
      balanceAmount += round2(b.balanceAmount);
    }
    return {
      count: billsBranch.length,
      totalAmount: round2(totalAmount),
      paidAmount: round2(paidAmount),
      balanceAmount: round2(balanceAmount),
    };
  }, [billsBranch]);

  const paymentsBranch = useMemo(
    () => payments.filter((p) => creditPaymentBranch(p, billById) === branch),
    [payments, billById, branch]
  );

  const name = bills[0]?.customerName || billsBranch[0]?.customerName || "Customer";
  const phone = bills[0]?.phone || billsBranch[0]?.phone || "";

  const receivedMetaByBillId = useMemo(() => {
    const map = new Map();
    for (const p of paymentsBranch) {
      const payDate = paymentTimestamp(p);
      const allocs = Array.isArray(p?.allocations) ? p.allocations : [];
      for (const a of allocs) {
        const billId = a?.billId;
        const amt = Number(a?.amount) || 0;
        if (!billId || amt <= 0) continue;
        if (!map.has(billId)) {
          map.set(billId, { last: payDate });
          continue;
        }
        const m = map.get(billId);
        if (payDate > m.last) m.last = payDate;
      }
    }
    return map;
  }, [paymentsBranch]);

  const saveAuditNote = async () => {
    const txt = noteText.trim();
    if (!txt) return;
    setSavingNote(true);
    setErr("");
    try {
      await addAuditNoteForCustomer({
        customerId,
        customerName: name,
        note: txt,
        lastContactDate,
        nextActionDate,
      });
      setNoteText("");
      await load();
    } catch (e) {
      setErr(e?.message || "Failed to save audit note.");
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100/90">
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-6xl px-3 pb-24 pt-4 sm:px-4">
        <div className="mb-4">
          <Link to="/credit-due" className="text-sm font-semibold text-indigo-600 hover:underline">
            ← Credit / Due
          </Link>
          <h1 className="mt-2 text-lg font-bold text-slate-900">{name}</h1>
          <p className="text-xs text-slate-600 font-mono">{customerId}</p>
          {phone ? <p className="text-sm text-slate-700">{phone}</p> : null}
          <div className="mt-3 max-w-xs">
            <CreditBranchSelector value={branch} onChange={setBranch} options={visibleBranches} />
            <p className="mt-1 text-[11px] text-slate-500">Bills and receipts below follow this branch.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_20rem] md:items-start">
          <aside className="order-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:sticky md:top-24">
            <h2 className="text-sm font-extrabold text-slate-900">Options</h2>

            <div className="mt-3 grid grid-cols-1 gap-2">
              <Link
                to={`/credit-due?receive=1&customerId=${encodeURIComponent(customerId)}`}
                className="rounded-lg bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow hover:bg-indigo-700"
              >
                Receive payment
              </Link>
              <Link
                to={`/credit-due/payments?customerId=${encodeURIComponent(customerId)}`}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                Payment history
              </Link>
              <Link
                to={`/credit-due/customer/${encodeURIComponent(customerId)}/timeline`}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-center text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                Customer timeline
              </Link>
              <button
                type="button"
                onClick={load}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                Refresh
              </button>
            </div>

            <div className="mt-4">
              <p className="text-[11px] font-bold uppercase text-slate-600">Days column</p>
              <div className="mt-2 space-y-2 text-sm font-semibold text-slate-800">
                <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <input
                    type="radio"
                    name="daysMode"
                    checked={daysMode === "received"}
                    onChange={() => {
                      setDaysMode("received");
                      try {
                        localStorage.setItem("creditCustomer_daysMode", "received");
                      } catch {
                        // ignore
                      }
                    }}
                  />
                  Received in (days)
                </label>
                <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <input
                    type="radio"
                    name="daysMode"
                    checked={daysMode === "credit"}
                    onChange={() => {
                      setDaysMode("credit");
                      try {
                        localStorage.setItem("creditCustomer_daysMode", "credit");
                      } catch {
                        // ignore
                      }
                    }}
                  />
                  Credit days
                </label>
              </div>
            </div>
          </aside>

          <section className="order-1 min-w-0">
            <div className="mb-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="mb-2 text-sm font-bold text-slate-900">Audit notes</h2>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <input
                  type="date"
                  value={lastContactDate}
                  onChange={(e) => setLastContactDate(e.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Last contact"
                />
                <input
                  type="date"
                  value={nextActionDate}
                  onChange={(e) => setNextActionDate(e.target.value)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Next action"
                />
                <button
                  type="button"
                  onClick={saveAuditNote}
                  disabled={savingNote || !noteText.trim()}
                  className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black disabled:opacity-50"
                >
                  {savingNote ? "Saving..." : "Save note"}
                </button>
              </div>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={3}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Call note / follow-up summary..."
              />
            </div>

            {err && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div>
            )}

            {loading ? (
              <p className="text-sm text-slate-600">Loading…</p>
            ) : (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-xs font-bold uppercase text-slate-600">Bills</p>
                </div>
                <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-bold uppercase text-slate-600">
                <tr>
                  <th className="p-2">Bill</th>
                  <th className="p-2">C/o</th>
                  <th className="p-2">Date</th>
                  <th className="p-2">Invoice / due</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2 text-right">Paid</th>
                  <th
                    className="p-2 text-right"
                    title={
                      daysMode === "credit"
                        ? "Days since invoice (or bill date) to today"
                        : "How many days it took to receive the amount that was paid"
                    }
                  >
                    {daysMode === "credit" ? "Credit days" : "Received in (days)"}
                  </th>
                  <th className="p-2 text-right">Balance</th>
                  <th className="p-2">Status</th>
                  <th className="p-2 text-center">Edit</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {billsBranch.map((b) => {
                  let overdue = false;
                  if (round2(b.balanceAmount) > 0) {
                    const eff = effectiveDueDateForOverdue(b);
                    if (eff) {
                      const t = new Date();
                      t.setHours(0, 0, 0, 0);
                      overdue = eff < t;
                    }
                  }
                  const creditDays = daysOutstandingForBill(b);
                  const recvMeta = receivedMetaByBillId.get(b.id);
                  const receivedDays =
                    round2(b.paidAmount) > 0 && recvMeta?.last ? diffDays(creditAgeStartDate(b), recvMeta.last) : null;
                  return (
                    <tr key={b.id} className={`border-t border-slate-100 ${overdue ? "bg-amber-50/50" : ""}`}>
                      <td className="p-2 font-mono text-xs">{b.voucherNo || b.id?.slice(0, 8)}</td>
                      <td className="p-2 text-xs text-slate-700 max-w-[10rem]">{b.careOf || "—"}</td>
                      <td className="p-2 text-xs">{fmtDate(b.createdAt ?? b.invoiceDate)}</td>
                      <td className="p-2 text-xs">{fmtDate(b.invoiceDate || b.dueDate)}</td>
                      <td className="p-2 text-right tabular-nums">₹{round2(b.totalAmount).toLocaleString("en-IN")}</td>
                      <td className="p-2 text-right tabular-nums">₹{round2(b.paidAmount).toLocaleString("en-IN")}</td>
                      <td className="p-2 text-right tabular-nums font-medium text-slate-800">
                        {daysMode === "credit"
                          ? creditDays.toLocaleString("en-IN")
                          : typeof receivedDays === "number"
                            ? receivedDays.toLocaleString("en-IN")
                            : "—"}
                      </td>
                      <td className="p-2 text-right font-semibold tabular-nums">₹{round2(b.balanceAmount).toLocaleString("en-IN")}</td>
                      <td className="p-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${statusClass(b.status)}`}>
                          {b.status || "pending"}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <Link
                          to={`/credit-due/bill/${encodeURIComponent(b.id)}/edit`}
                          className="text-[10px] font-semibold text-indigo-600 hover:underline"
                        >
                          Edit
                        </Link>
                      </td>
                      <td className="p-2">
                        {round2(b.balanceAmount) > 0 && phone ? (
                          <a
                            href={
                              whatsappUrlForPhone(
                                phone,
                                buildWhatsAppReminderMessage({
                                  customerName: name,
                                  voucherNo: b.voucherNo,
                                  balanceAmount: b.balanceAmount,
                                  dueDate: b.dueDate,
                                  invoiceDate: b.invoiceDate,
                                })
                              ) || "#"
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] font-semibold text-emerald-700 hover:underline"
                          >
                            WhatsApp
                          </a>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {billsBranchTotals.count > 0 ? (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold text-slate-900">
                    <td className="p-2 text-left text-xs font-bold uppercase tracking-wide text-slate-600" colSpan={4}>
                      Totals ({billsBranchTotals.count} bill{billsBranchTotals.count === 1 ? "" : "s"})
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      ₹{billsBranchTotals.totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      ₹{billsBranchTotals.paidAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-2 text-right tabular-nums font-medium text-slate-500">—</td>
                    <td className="p-2 text-right tabular-nums text-indigo-950">
                      ₹{billsBranchTotals.balanceAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="p-2" colSpan={3} />
                  </tr>
                </tfoot>
              ) : null}
            </table>
                {billsBranch.length === 0 && (
                  <p className="p-6 text-center text-sm text-slate-600">No bills for this customer on the selected branch.</p>
                )}
          </div>
            )}

            
          </section>
        </div>
      </main>
    </div>
  );
}
